import { ProtoImageConfig, ProtoImageType } from 'src/app/@shared/proto-image/proto-image.config';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

export type ProtoImageUrls = { [key in ProtoImageType | 'preload']?: string };

export type ProtoImageSource = {
  imageUrl: string;
  type: ProtoImageType;
};

export class ProtoImageModel {
  public id: string;
  public type: ProtoImageType;
  public urls: ProtoImageUrls;

  // imageUrl is set when image is provided via external url
  constructor(source: Partial<ProtoImageSource>) {
    this.id = source.imageUrl;
    this.type = source.type || 'default';
    this.urls = this.getImageUrls(source);
  }

  // eslint-disable-next-line max-lines-per-function,max-statements
  private getImageUrls(source: Partial<ProtoImageSource>): ProtoImageUrls {
    const urls: ProtoImageUrls = {};

    if (!source.imageUrl) {
      console.warn('no image url set');

      return urls;
    }

    if (source.imageUrl.startsWith('./') || source.imageUrl.startsWith('https')) {
      return {
        original: source.imageUrl,
        [this.type]: source.imageUrl,
      };
    }

    urls.original = ProtoUrl.filesDownload(source.imageUrl);

    // provide image urls with proper dimensions based on image type
    const sizes = ProtoImageConfig.size;
    const preloadImageWidth = 12;

    if (!sizes[this.type]) {
      this.type = 'default';
    }

    // for "default" use "detail" width, but auto height!
    if (this.type === 'default') {
      urls[this.type] = ProtoUrl.filesDownload(source.imageUrl, {
        queryParams: {
          width: sizes.detail.width,
        },
      });
      urls.preload = ProtoUrl.filesDownload(source.imageUrl, {
        queryParams: {
          width: preloadImageWidth,
        },
      });

      return urls;
    }

    urls[this.type] = ProtoUrl.filesDownload(source.imageUrl, {
      queryParams: {
        height: sizes[this.type].height,
        width: sizes[this.type].width,
      },
    });

    // we don't need preload images for avatar
    if (this.type === 'avatar') {
      return urls;
    }

    // cache "detail" (!) preload images in thumbnails (see cachePreloadImage comment)
    const key = this.type === 'thumbnail'
      ? 'detail'
      : this.type;

    // ensure preload image has same ratio as target image
    const preloadImageFactor = sizes[key].width / sizes[key].height;
    const preloadImageHeight = Math.ceil(preloadImageWidth / preloadImageFactor);

    urls.preload = ProtoUrl.filesDownload(source.imageUrl, {
      queryParams: {
        height: preloadImageHeight,
        width: preloadImageWidth,
      },
    });

    return urls;
  }
}
